import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiRequestError } from '../lib/api-client';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { Panel } from '../ui/Page';
import {
  disconnectChannel,
  importContacts,
  listSessions,
  pairChannel,
  type ImportResult,
  type SessionView,
} from './whatsapp-web-api';
import { QrCode } from './QrCode';
import { countdown, pairingView } from './pairing';
import type { ChannelView } from './api';
import { useRealtimeTopic } from '../app/realtime-store';

/**
 * Unofficial WhatsApp, on the screen (context.md §68). Phase 58.
 *
 * Since Phase 108 this panel no longer states a session's status: the channel table above carries
 * it, read from the same session row. A screen that states one fact in two places invites the two
 * to disagree, and this one did — "Desconectado" in the table, "Conectado" here, about the same
 * number. What is left here is what only this panel can do: warn before somebody pairs, draw the
 * code, and act on a number that is already paired.
 *
 * The thing this screen exists to do is state the risk before somebody pairs, and to keep stating
 * what the session actually is afterwards. Everything else — a code, a status, a disconnect button —
 * any CRM has.
 *
 * Two decisions worth reading:
 *
 * **The warning is a step, not a footnote.** Pairing attaches a device to somebody's personal
 * WhatsApp account, and if it goes wrong the number that gets banned is the customer's — the one on
 * their vans, their invoices and their shop front. A sentence in grey under a button is not how you
 * tell somebody that. So it is a modal they have to read and tick, and the tick is not remembered:
 * the second number is as much at risk as the first.
 *
 * **Health is shown, not status.** The row saying `CONNECTED` is a claim; `HEALTHY` means the engine
 * answered recently. A session that has gone quiet says so here, because a tenant learning it from a
 * customer complaint is the failure the distinction exists to prevent.
 *
 * **The code is drawn, not printed.** WhatsApp hands over a string of a few hundred characters and
 * the screen used to show it verbatim. Nobody can scan text, so it is rendered as a QR code here,
 * in the browser — the payload is a credential and has no business being posted to an image service
 * to be turned into a picture.
 *
 * **A development engine says so.** The in-process double pairs instantly and returns a placeholder:
 * well-formed, and impossible for any phone to read. Drawing it as a QR code would send somebody to
 * hold their phone up to a square that will never work, and they would reasonably conclude the
 * product is broken.
 *
 * **A plan that does not include this offers no buttons.** A screen that walks somebody through a
 * risk warning, a tick box and a confirm, only to answer "not on your plan", has wasted their time
 * and taught them to distrust the next warning it shows.
 */

/**
 * A refusal that will not change on its own (Phase 130).
 *
 * Entitlement changes when somebody changes plan, not on its own, so once the answer is "not
 * entitled", asking again in twenty seconds — and again on every realtime frame — produces the same
 * 402 forever. It filled the console of the first production tenant to open this screen, and each
 * one of those is a request the API answered for nothing.
 */
function isPlanRefusal(error: unknown): boolean {
  return error instanceof ApiRequestError && error.code === 'billing.feature_not_entitled';
}

export function WhatsAppWebPanel({
  channels,
}: {
  channels: readonly ChannelView[];
}): React.JSX.Element | null {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  /*
   * A second hand (Phase 136).
   *
   * The countdown and the "há N segundos" have to move on their own: the sessions query refetches
   * every twenty seconds, and a number that only changes three times a minute reads as a screen
   * that has stopped. This is a clock, not a fetch — it costs one render a second and only while
   * somebody is looking at this panel.
   */
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), 1000);

    return () => clearInterval(timer);
  }, []);
  const [pairing, setPairing] = useState<ChannelView | null>(null);

  const unofficial = channels.filter((channel) => channel.provider === 'whatsapp_web');

  // Only asked when there is something to ask about, and a refusal is "not on this plan" rather
  // than an error — which since ADR 0041 is both true and something the tenant can act on.
  const sessions = useQuery({
    queryKey: ['whatsapp-web-sessions'],
    queryFn: listSessions,
    enabled: unofficial.length > 0,
    retry: false,
    /*
     * The session service says when the code was scanned (Phase 80).
     *
     * This is the one screen where somebody is standing there with a phone in their hand, and the
     * ten-second poll it replaces meant scanning the code and seeing it work were up to ten seconds
     * apart — long enough to scan it a second time. The poll stays as a slow floor, because a
     * pairing code lives about a minute and a socket that is quietly down must not cost the pairing.
     */
    refetchInterval: (query) => (isPlanRefusal(query.state.error) ? false : 20_000),
  });

  const blocked = isPlanRefusal(sessions.error);

  useRealtimeTopic(unofficial.length > 0 && !blocked ? 'whatsapp_session' : null, () => {
    void client.invalidateQueries({ queryKey: ['whatsapp-web-sessions'] });
  });

  /*
   * Why the list could not be read.
   *
   * A plan refusal is not an error: it is the answer, and it is the only thing this panel should
   * then say. Anything else — the service being down, a network blip — is reported separately,
   * because the two lead somewhere completely different.
   */
  const refusal = blocked ? 'plan' : sessions.isError ? 'unavailable' : null;

  /*
   * The in-process double. It pairs, it reports health, it exercises this whole screen — and its
   * pairing code is a placeholder. Saying so is the difference between "not configured yet" and
   * "this product cannot connect WhatsApp".
   */
  const simulated = sessions.data?.engine === 'local';

  const pair = useMutation({
    mutationFn: (channelId: string) => pairChannel(channelId),
    onSuccess: () => {
      setError(null);
      setPairing(null);
      void client.invalidateQueries({ queryKey: ['whatsapp-web-sessions'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  /*
   * The address book, brought in once.
   *
   * Reported afterwards with the counts rather than a bare "pronto": how many were already there is
   * the number that tells somebody whether running it again is worth anything, and how many were
   * skipped is what explains a total that does not match their phone.
   */
  const [imported, setImported] = useState<ImportResult | null>(null);

  const importing = useMutation({
    mutationFn: (channelId: string) => importContacts(channelId),
    onSuccess: (result) => {
      setError(null);
      setImported(result);
    },
    onError: (caught: unknown) => {
      setImported(null);
      setError(messageFor(caught));
    },
  });

  const disconnect = useMutation({
    mutationFn: (channelId: string) => disconnectChannel(channelId),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['whatsapp-web-sessions'] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  if (unofficial.length === 0) return null;

  const sessionFor = (channelId: string): SessionView | undefined =>
    sessions.data?.sessions.find((session) => session.channel_id === channelId);

  return (
    <Panel title={t('whatsappWeb.title')}>
      {/* The risk warning is for somebody who can actually pair. On a plan without the feature it
          is a warning about something that cannot happen, so the refusal takes its place. */}
      {blocked ? (
        <p className="notice">
          <span>{t('whatsappWeb.notOnPlan')}</span>
        </p>
      ) : (
        <p className="notice notice--warning">
          <span>{t('whatsappWeb.experimental')}</span>
        </p>
      )}

      {refusal === 'unavailable' && <p className="muted small">{t('whatsappWeb.unavailable')}</p>}

      {simulated && !blocked && (
        <p className="notice" role="status">
          <span>{t('whatsappWeb.simulated')}</span>
        </p>
      )}

      {/* Suppressed while blocked: the notice above already says it, and saying it twice in two
          different wordings reads as two different problems. */}
      {error && !blocked && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {imported && (
        <p className="notice" role="status">
          {t('whatsappWeb.imported')
            .replace('{created}', String(imported.created))
            .replace('{existing}', String(imported.existing))
            .replace('{skipped}', String(imported.skipped))}
          {imported.truncated && ` ${t('whatsappWeb.importTruncated')}`}
        </p>
      )}

      <ul className="sessions">
        {unofficial.map((channel) => {
          const session = sessionFor(channel.id);
          const live = session?.health === 'HEALTHY' || session?.health === 'STALE';

          return (
            <li key={channel.id} className="session">
              <span className="cell-stack">
                <span className="strong">{channel.name}</span>
                <span className="muted small">
                  {session?.phone_number ?? channel.address ?? t('whatsappWeb.notPaired')}
                </span>
              </span>

              {/*
               * No status here any more (Phase 108).
               *
               * The table above carries it, from the same session this panel reads, and a screen
               * that states the same fact twice invites the two to disagree — which is exactly
               * what happened: "Desconectado" in the table, "Conectado" here, about one number.
               * What is left is what only this panel can do: pair, and act on a paired number.
               */}
              {/* A code is shown once and lives a minute; it is a credential, not a status. */}
              {session && <Pairing session={session} simulated={simulated} tick={tick} />}

              <span className="cell-inline">
                {session?.status === 'BANNED' ? (
                  <span className="muted small">{t('whatsappWeb.banned')}</span>
                ) : (
                  <button
                    type="button"
                    className="button button--quiet button--compact"
                    disabled={pair.isPending || blocked}
                    title={blocked ? t('whatsappWeb.notOnPlan') : undefined}
                    onClick={() => setPairing(channel)}
                  >
                    {live ? t('whatsappWeb.pairAgain') : t('whatsappWeb.pair')}
                  </button>
                )}

                {live && (
                  <>
                    {/* Only while it is connected: the address book lives in the session. */}
                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      disabled={importing.isPending || blocked}
                      onClick={() => importing.mutate(channel.id)}
                    >
                      {importing.isPending
                        ? t('whatsappWeb.importing')
                        : t('whatsappWeb.importContacts')}
                    </button>

                    <button
                      type="button"
                      className="button button--quiet button--compact"
                      disabled={disconnect.isPending || blocked}
                      onClick={() => disconnect.mutate(channel.id)}
                    >
                      {t('whatsappWeb.disconnect')}
                    </button>
                  </>
                )}
              </span>
            </li>
          );
        })}
      </ul>

      {pairing && (
        <RiskDialog
          channelName={pairing.name}
          pending={pair.isPending}
          onConfirm={() => pair.mutate(pairing.id)}
          onClose={() => setPairing(null)}
        />
      )}
    </Panel>
  );
}

/**
 * What somebody has to read before the first scan.
 *
 * Deliberately not dismissible by clicking away, and the confirm stays disabled until the box is
 * ticked. This is the one dialog in the product where making it slightly annoying is the point: the
 * cost of getting it wrong lands on the customer's own phone number, and they should have decided
 * that on purpose.
 */
function RiskDialog({
  channelName,
  pending,
  onConfirm,
  onClose,
}: {
  channelName: string;
  pending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}): React.JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  const [understood, setUnderstood] = useState(false);

  useEffect(() => {
    const element = dialog.current;

    if (element && !element.open) element.showModal();
  }, []);

  return (
    <dialog
      ref={dialog}
      className="confirm confirm--wide"
      aria-label={t('whatsappWeb.riskTitle')}
      onCancel={(cancelEvent) => {
        cancelEvent.preventDefault();
        onClose();
      }}
      onClose={onClose}
    >
      <form
        onSubmit={(submitEvent) => {
          submitEvent.preventDefault();

          if (understood) onConfirm();
        }}
      >
        <h2>{t('whatsappWeb.riskTitle')}</h2>
        <p className="muted">{t('whatsappWeb.riskChannel').replace('{name}', channelName)}</p>

        <ul className="risk-list">
          <li>{t('whatsappWeb.risk.unofficial')}</li>
          <li>{t('whatsappWeb.risk.ban')}</li>
          <li>{t('whatsappWeb.risk.session')}</li>
          <li>{t('whatsappWeb.risk.noTemplates')}</li>
          <li>{t('whatsappWeb.risk.official')}</li>
        </ul>

        <label className="field field--inline">
          <input
            type="checkbox"
            checked={understood}
            onChange={(changeEvent) => setUnderstood(changeEvent.target.checked)}
          />
          <span>{t('whatsappWeb.riskAccept')}</span>
        </label>

        <div className="confirm__actions">
          <button type="button" className="button button--quiet button--compact" onClick={onClose}>
            {t('confirm.cancel')}
          </button>

          <button
            type="submit"
            className="button button--primary button--compact"
            disabled={!understood || pending}
          >
            {pending ? t('work.creating') : t('whatsappWeb.riskConfirm')}
          </button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * The pairing step, said out loud.
 *
 * Three states and each gets its own words, because they ask for different things from the person
 * in front of the screen: scan this (and here is how long it lives), the code lapsed (ask for
 * another), or your phone has scanned it and WhatsApp is copying the account across — which takes
 * minutes and used to be indistinguishable from nothing happening.
 */
function Pairing({
  session,
  simulated,
  tick,
}: {
  session: SessionView;
  simulated: boolean;
  tick: number;
}): React.JSX.Element | null {
  const view = pairingView(session, new Date(tick));

  if (!view) return null;

  if (view.kind === 'syncing') {
    return (
      <span className="cell-stack session__pairing">
        <span className="strong">{t('whatsappWeb.syncing')}</span>

        {/* A bar that never arrives, on purpose: see `syncProgress`. */}
        <span
          className="sync-bar"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={Math.round(view.progress * 100)}
        >
          <span
            className="sync-bar__fill"
            style={{ width: `${Math.round(view.progress * 100)}%` }}
          />
        </span>

        <span className="muted small">
          {t('whatsappWeb.syncingHint').replace('{elapsed}', countdown(view.elapsed))}
        </span>
      </span>
    );
  }

  if (view.kind === 'expired') {
    return (
      <span className="cell-stack session__pairing">
        <span className="muted small">{t('whatsappWeb.codeExpired')}</span>
      </span>
    );
  }

  return (
    <span className="cell-stack session__pairing">
      {simulated ? (
        /* No QR for a payload no phone can read — the placeholder is shown as what it is, so
           nobody spends five minutes scanning it. */
        <span className="muted small">{t('whatsappWeb.simulatedCode')}</span>
      ) : (
        <>
          <QrCode payload={session.pairing_code!} />
          <span className="muted small">{t('whatsappWeb.scan')}</span>
        </>
      )}

      <span className={view.secondsLeft <= 10 ? 'small strong' : 'muted small'}>
        {t('whatsappWeb.codeCountdown').replace('{left}', countdown(view.secondsLeft))}
      </span>
    </span>
  );
}
